/*
 * System 组件的类型定义
 */

import { Entity } from '@backstage/catalog-model';

/**
 * 环境配置 JSON 格式（存储在 annotations 中）
 * 示例: {"server":"https://101.42.125.249:443","namespace":"nginx-demo","name":"tx-ops-test","clusterName":"tx-ops-test"}
 */
export interface EnvironmentDestination {
  server: string;         // K8s API Server 地址
  namespace: string;      // 命名空间
  name?: string;          // Destination 名称（可选）
  clusterName?: string;   // K8s 集群名称（可选，可能与 name 相同）
}

/**
 * 环境配置信息（解析后）
 * 从 System 实体的 annotations 中解析
 * 支持两种格式:
 * 1. JSON: {"server":"...","namespace":"...","name":"...","clusterName":"..."}
 * 2. 管道符分隔: clusterName|server|namespace
 */
export interface EnvironmentConfig {
  name: string;           // 环境名称: test, staging, prod
  label: string;          // 显示标签: TEST, STAGING, PROD
  clusterName: string;    // K8s 集群名称
  server: string;         // K8s API Server 地址
  namespace: string;      // 命名空间
}

/**
 * ArgoCD Project 关联信息
 */
export interface ArgoCDProjectInfo {
  instanceName: string;   // ArgoCD 实例名称
  projectName: string;    // ArgoCD Project 名称
  resourceRef: string;    // Backstage Resource 引用
}

/**
 * 组件部署状态
 */
export interface ComponentDeploymentStatus {
  componentName: string;
  componentRef: string;
  environment: string;
  syncStatus: 'Synced' | 'OutOfSync' | 'Unknown';
  healthStatus: 'Healthy' | 'Progressing' | 'Degraded' | 'Suspended' | 'Missing' | 'Unknown';
  lastSyncTime?: string;
}

/**
 * System 下的 Component 信息
 */
export interface SystemComponent {
  name: string;
  entityRef: string;
  type: string;
  owner: string;
  description?: string;
  lifecycle?: string;
  deployments: ComponentDeploymentStatus[];
}

/**
 * 解析单个环境配置值
 * 支持两种格式:
 * 1. JSON: {"server":"...","namespace":"...","name":"...","clusterName":"..."}
 *    或 Knative 格式: {"clusterName":"...","clusterUrl":"...","namespace":"...","environmentType":"..."}
 * 2. 管道符分隔: clusterName|server|namespace
 */
function parseEnvironmentValue(value: string): EnvironmentDestination | null {
  // 尝试解析 JSON 格式
  if (value.startsWith('{')) {
    try {
      const parsed = JSON.parse(value);
      // 支持 server 或 clusterUrl 作为服务器地址
      const server = parsed.server || parsed.clusterUrl;
      if (server && parsed.namespace) {
        return {
          server,
          namespace: parsed.namespace,
          name: parsed.name,
          clusterName: parsed.clusterName,
        };
      }
    } catch {
      // JSON 解析失败，尝试管道符格式
    }
  }

  // 尝试解析管道符分隔格式: clusterName|server|namespace
  const parts = value.split('|');
  if (parts.length >= 3) {
    return {
      clusterName: parts[0],
      server: parts[1],
      namespace: parts[2],
    };
  }

  return null;
}

/**
 * 获取 System 的管理方式
 * 判断逻辑：
 * 1. hose.io/powered-by: knative → Knative
 * 2. hose.io/powered-by: ssa → SSA
 * 3. 其他（默认）→ ArgoCD（兼容已上线的老数据）
 */
export function getManagementType(entity: Entity): 'knative' | 'argocd' | 'ssa' {
  const annotations = entity.metadata?.annotations || {};
  const poweredBy = annotations['hose.io/powered-by'];
  
  if (poweredBy === 'knative') return 'knative';
  if (poweredBy === 'ssa') return 'ssa';
  return 'argocd'; // 默认 ArgoCD，兼容老数据
}

/**
 * 检查 System 是否是 Knative 管理
 */
export function isKnativeManaged(entity: Entity): boolean {
  return getManagementType(entity) === 'knative';
}

/**
 * 解析 Knative 环境配置
 * 从 hose.io/knative-dest-* 注解解析
 */
export function parseKnativeEnvironmentConfigs(entity: Entity): EnvironmentConfig[] {
  const annotations = entity.metadata?.annotations || {};
  const environments: EnvironmentConfig[] = [];

  const envMappings = [
    { key: 'hose.io/knative-dest-test', name: 'test', label: 'TEST' },
    { key: 'hose.io/knative-dest-staging', name: 'staging', label: 'STAGING' },
    { key: 'hose.io/knative-dest-prod', name: 'prod', label: 'PROD' },
  ];

  for (const mapping of envMappings) {
    const value = annotations[mapping.key];
    if (value) {
      const dest = parseEnvironmentValue(value);
      if (dest) {
        environments.push({
          name: mapping.name,
          label: mapping.label,
          clusterName: dest.clusterName || dest.name || extractHostFromServer(dest.server),
          server: dest.server,
          namespace: dest.namespace,
        });
      }
    }
  }

  return environments;
}

/**
 * 解析 System 实体的环境配置
 * 优先检查是否是 Knative 管理，如果是则解析 Knative 配置，否则解析 ArgoCD 配置
 */
export function parseEnvironmentConfigs(entity: Entity): EnvironmentConfig[] {
  // 如果是 Knative 管理，解析 Knative 环境配置
  if (isKnativeManaged(entity)) {
    return parseKnativeEnvironmentConfigs(entity);
  }

  // 否则解析 ArgoCD 环境配置
  const annotations = entity.metadata?.annotations || {};
  const environments: EnvironmentConfig[] = [];

  const envMappings = [
    { key: 'hose.io/argocd-dest-test', name: 'test', label: 'TEST' },
    { key: 'hose.io/argocd-dest-staging', name: 'staging', label: 'STAGING' },
    { key: 'hose.io/argocd-dest-prod', name: 'prod', label: 'PROD' },
  ];

  for (const mapping of envMappings) {
    const value = annotations[mapping.key];
    if (value) {
      const dest = parseEnvironmentValue(value);
      if (dest) {
        environments.push({
          name: mapping.name,
          label: mapping.label,
          // 优先使用 clusterName，其次使用 name，最后使用 server 的简化形式
          clusterName: dest.clusterName || dest.name || extractHostFromServer(dest.server),
          server: dest.server,
          namespace: dest.namespace,
        });
      }
    }
  }

  return environments;
}

/**
 * 从 server URL 提取主机名作为集群标识
 */
function extractHostFromServer(server: string): string {
  try {
    const url = new URL(server);
    return url.hostname;
  } catch {
    return server;
  }
}

/**
 * Knative Resource 引用信息
 */
export interface KnativeResourceInfo {
  environment: string;  // test, staging, prod
  label: string;        // TEST, STAGING, PROD
  resourceRef: string;  // resource:default/knative-xxx
  resourceName: string; // knative-xxx
}

/**
 * 从 System 实体获取 Knative Resource 引用列表
 * 从 hose.io/knative-resource-* annotations 解析
 */
export function getKnativeResourceRefs(entity: Entity): KnativeResourceInfo[] {
  const annotations = entity.metadata?.annotations || {};
  const resources: KnativeResourceInfo[] = [];

  const envMappings = [
    { key: 'hose.io/knative-resource-test', environment: 'test', label: 'TEST' },
    { key: 'hose.io/knative-resource-staging', environment: 'staging', label: 'STAGING' },
    { key: 'hose.io/knative-resource-prod', environment: 'prod', label: 'PROD' },
  ];

  for (const mapping of envMappings) {
    const value = annotations[mapping.key];
    if (value) {
      // 格式: resource:default/knative-xxx
      const resourceName = value.split('/').pop() || value;
      resources.push({
        environment: mapping.environment,
        label: mapping.label,
        resourceRef: value,
        resourceName,
      });
    }
  }

  return resources;
}

/**
 * 从 System 实体获取 ArgoCD Project 信息
 */
export function getArgoCDProjectInfo(entity: Entity): ArgoCDProjectInfo | null {
  const dependsOn = entity.spec?.dependsOn as string[] | undefined;
  if (!dependsOn || dependsOn.length === 0) {
    return null;
  }

  // 查找 argocd-project 类型的 Resource
  // 格式: resource:default/argocd-{instance}-{project}
  const argocdProjectRef = dependsOn.find(ref => 
    ref.startsWith('resource:') && ref.includes('argocd-')
  );

  if (!argocdProjectRef) {
    return null;
  }

  // 解析 resource:default/argocd-development-payment
  const refParts = argocdProjectRef.replace('resource:', '').split('/');
  if (refParts.length < 2) {
    return null;
  }

  const resourceName = refParts[refParts.length - 1];
  // argocd-development-payment -> instance=development, project=payment
  const match = resourceName.match(/^argocd-([^-]+)-(.+)$/);
  
  if (!match) {
    return null;
  }

  return {
    instanceName: match[1],
    projectName: match[2],
    resourceRef: argocdProjectRef,
  };
}

/**
 * 环境状态颜色映射
 */
export const environmentColors: Record<string, { bg: string; text: string; border: string }> = {
  test: { bg: '#e3f2fd', text: '#1565c0', border: '#1976d2' },
  staging: { bg: '#e0f7fa', text: '#00838f', border: '#00acc1' },  // 青色
  prod: { bg: '#e8f5e9', text: '#2e7d32', border: '#4caf50' },
};

/**
 * 同步状态颜色
 */
export const syncStatusColors: Record<string, string> = {
  Synced: '#4caf50',
  OutOfSync: '#ff9800',
  Unknown: '#9e9e9e',
};

/**
 * 健康状态颜色
 */
export const healthStatusColors: Record<string, string> = {
  Healthy: '#4caf50',
  Progressing: '#2196f3',
  Degraded: '#f44336',
  Suspended: '#ff9800',
  Missing: '#9e9e9e',
  Unknown: '#9e9e9e',
};

